import {IFormController, IOnChanges, IOnChangesObject, IOnDestroy, IOnInit, ITimeoutService} from "angular";
import {PortfolioPositions} from "../../../../../models/Portfolio";
import {OrderVettingService} from "../../../services/OrderVettingService";
import {OptionsTicket} from "../../../types/OptionsTicket";
import {OrderVettingResponse, Premium, TradeFees, VettingOutcomes} from "../../../types/OrderVettingResponse";
import {CurrentUserService} from "../../../../../../services/user/CurrentUserService";
import {fromTmcTicket} from "../../../services/models/PlaceOrderRequest";
import {MarginQuizService} from "../../../../../../quiz/margin/service/MarginQuizService";
import {AccountStore} from "../../../../../../account/store/AccountStore";
import {Observable, ReplaySubject, Scheduler} from "rxjs";
import {QuoteRequests} from "../../../../../../quote/models/QuoteRequest";
import {SecurityUtil} from "../../../../../../js/util/SecurityUtil";
import {QuoteStore} from "../../../../../../quote/store/QuoteStore";
import {distinct} from "../../../../../../js/util/UtilFunctions";

export abstract class OrderConfirmComponentController implements IOnInit, IOnChanges, IOnDestroy {

    /** @input */
    public set portfolio(portfolio: PortfolioPositions | null) {
        if (portfolio != null) {
            this.portfolio$.next(portfolio);
        }
    }

    public readonly portfolio$ = new ReplaySubject<PortfolioPositions>(1);

    /** @require */
    public readonly form: IFormController;

    public isClient: boolean;

    public totalPremium: number;
    public changeInMargin: number;
    public marginLimit: number;
    public clientMargin: number;
    public totalCost: number;
    public currentBalance: number;
    public whatIfBalance: number;
    public cashAfterTrade: number;
    public changeInCash: number;
    public feesAndBrokerage: number;
    public tradeFees: TradeFees;
    public vettingResult: OrderVettingResponse;

    private readonly underlying$ = new ReplaySubject<ReadonlyArray<string>>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    protected constructor(protected readonly $timeout: ITimeoutService,
                          protected readonly currentUserService: CurrentUserService,
                          protected readonly orderVettingService: OrderVettingService,
                          protected readonly marginQuizService: MarginQuizService,
                          protected readonly accountStore: AccountStore,
                          protected readonly quoteStore: QuoteStore) {
    }

    public $onInit() {
        this.form.$setValidity("initialized", false, this.form);
        this.isClient = this.currentUserService.isClient();

        const quotes$ = this.underlying$
                            .withLatestFrom(this.portfolio$)
                            .observeOn(Scheduler.async)
                            .takeUntil(this.destroyed$)
                            .map(([underlyings, portfolio]) => this.buildRequests(underlyings, portfolio))
                            .switchMap((requests) => this.quoteStore.stream(requests));

        Observable.combineLatest(quotes$, this.portfolio$)
                  .observeOn(Scheduler.async)
                  .takeUntil(this.destroyed$)
                  .subscribe(([_, portfolio]) => this.validateOrder(portfolio));
    }

    private buildRequests(underlyings: ReadonlyArray<string>, portfolio: PortfolioPositions): QuoteRequests {
        return underlyings.map((u) => {
            return {
                code: u,
                exchange: SecurityUtil.guessExchange(u),
                portfolioType: portfolio.portfolioType
            };
        });
    }

    public async $onChanges(changes: IOnChangesObject) {
        const trades = this.orderTickets();
        const underlyings = trades.map((t) => t.code)
                                  .map((c) => SecurityUtil.underlying(c))
                                  .filter(distinct);
        this.underlying$.next(underlyings);
    }

    private async validateOrder(portfolio: PortfolioPositions) {
        const trades = this.orderTickets();
        const requests = trades.map(fromTmcTicket);
        const vettingResult = await this.orderVettingService.validate(portfolio.id, requests);
        this.vettingResult = vettingResult;

        const scenarioResult = vettingResult;
        const scenarioSummary = scenarioResult.scenarioSummary;

        this.totalPremium = scenarioSummary.totalPremium;
        this.changeInMargin = scenarioResult.changeInMargin;
        this.totalCost = scenarioSummary.totalCost;
        this.currentBalance = scenarioSummary.currentBalance;
        this.whatIfBalance = scenarioSummary.whatIfBalance;
        this.cashAfterTrade = this.whatIfBalance;
        this.changeInCash = scenarioSummary.changeInCash;
        this.clientMargin = scenarioSummary.clientMargin;
        this.marginLimit = portfolio.marginLimit || 0;

        this.tradeFees = scenarioSummary.fees;
        this.feesAndBrokerage = Object.values(this.tradeFees)
                                      .reduce((acc, next) => acc + next, 0);
        this.addPremiumToTrades(scenarioResult.tradePremiums);
        this.setValidationState(vettingResult.vettingOutcomes);

        this.$timeout(() => this.form.$setValidity("initialized", true, this.form));
    }

    protected abstract orderTickets(): Array<OptionsTicket>;

    protected addPremiumToTrades(premium: ReadonlyArray<Premium>) {
        premium.forEach(p => this.addPremiumToTrade(p));
    }

    protected abstract addPremiumToTrade(premium: Premium): void;

    protected setValidationState(vettingOutcomes: VettingOutcomes) {
        const isInValid = vettingOutcomes.errors;
        this.form.$setValidity("order-vetting", !isInValid, this.form);
    }

    public async takeMarginQuiz() {
        await this.marginQuizService.open();
        this.accountStore.refresh();
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
